import { Skeleton, Typography } from "@mui/material";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import {
  MixMaterial,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";
import { PlanId } from "../boundary/PlanId";
import { Period } from "hooks/periodIndex";
import { getDriMaterial } from "./utils";

interface Props {
  planId: PlanId | null;
  period: Period;
  mixId: number;
  massScaleFactor: number;
  preloadedDri?: MixMaterial | null;
  loading?: boolean;
}

const DriQuantity = ({
  material,
  massScaleFactor,
  loading,
}: {
  material: MixMaterial | null;
  massScaleFactor: number;
  loading: boolean;
}) => {
  const { format } = useNumberSerialiser({ decimalPlaces: 0 });
  return loading ? (
    <Skeleton width={25} />
  ) : (
    <Typography variant="body1Mono">
      {material === null
        ? null
        : format(material.quantity.value * massScaleFactor)}
    </Typography>
  );
};

const PlanDriCell = ({
  planId,
  period,
  mixId,
  massScaleFactor,
}: Omit<Props, "planId"> & { planId: PlanId }) => {
  const [, summary] = useSummarisePlanResultsMutation({
    fixedCacheKey: `${planId}-${period}`,
  });
  const mix =
    summary.status === QueryStatus.fulfilled
      ? summary.data.mixes.find((mix) => mix.original_mix_id === mixId)
      : undefined;
  return (
    <DriQuantity
      material={mix ? (getDriMaterial(mix.mix) ?? null) : null}
      massScaleFactor={massScaleFactor}
      loading={summary.status === QueryStatus.pending}
    />
  );
};

export const DRICell = ({
  planId,
  preloadedDri,
  loading = false,
  ...props
}: Props) =>
  preloadedDri !== undefined || planId === null || loading ? (
    <DriQuantity
      material={preloadedDri ?? null}
      massScaleFactor={props.massScaleFactor}
      loading={loading}
    />
  ) : (
    <PlanDriCell {...props} planId={planId} />
  );
